import { FC, ReactNode, useEffect, useState } from "react";
import { msg } from "@lingui/macro";
import { Box } from "@otrium/core";
import { capitalize } from "lodash-es";
import { orderBy } from "lodash";
import { Hit, SearchState } from "react-instantsearch-core";
import { LabelNavigation } from "src/molecules/LabelNavigation";
import { BrandFilter } from "src/organisms/AlgoliaProductList/MobileFilterView/BrandFilter";
import { availableFiltersList } from "src/organisms/AlgoliaProductList/availableFilters";
import {
  BRAND_FACET,
  CATEGORIES_FACET,
  COLOUR_FACET,
  CONSCIOUS_FACET,
  DISCOUNT_FACET,
  PATTERN_FACET,
  PRICE_FACET,
  SIZE_FACET,
  STYLE_FACET,
} from "src/utils/algolia";
import { MultiselectCategoryWrapper } from "src/organisms/AlgoliaProductList/MultiselectCategories";
import { MultiselectHitType } from "src/types/Algolia";
import { useSegment } from "src/hooks/useSegment";
import { DiscountFilter } from "src/organisms/AlgoliaProductList/MobileFilterView/DiscountFilter";
import { ClearAllRefinements } from "./ClearAllRefinements";
import { MenuItem } from "./MenuItem";
import DrawerFilter from "./DrawerFilter/DrawerFilter";
import DefaultFilter from "./DefaultFilter/DefaultFilter";
import PriceFilter from "./PriceFilter/PriceFilter";
import ColorFilter from "./ColorFilter/ColorFilter";
import { ResultStats } from "./ResultStats";
import { GoodOnYouFilter } from "./GoodOnYouFilter";
import { ContentWrapper, FilterContent, Wrapper } from "./MobileFilters.styled";
import { ArrowLeft } from "./DrawerFilter/DrawerFilter.styled";
import { MessageDescriptor } from "@lingui/core";

interface FiltersListItem {
  name: MessageDescriptor;
  facet: string;
  icon: ReactNode;
}

interface MobileFilterViewProps {
  handleClose: () => void;
  onResetFilters: () => void;
  genderId: string;
  excludedFacetsList?: string[];
  selectedCatSlug: string[];
  setStatePayload?: (
    payload: SearchState & { selectedCatSlug: string[] }
  ) => void;
  departmentSlug?: string;
  isNewPlp: boolean;
  categoriesTree?: MultiselectHitType[];
  isMultiselectCategories: boolean;
  keepSelectedIgnoreInActive?: { [p: string]: string[] };
}

const MobileFilterView: FC<MobileFilterViewProps> = ({
  handleClose,
  onResetFilters,
  genderId,
  excludedFacetsList = [],
  selectedCatSlug,
  setStatePayload,
  departmentSlug,
  isNewPlp,
  categoriesTree,
  isMultiselectCategories,
  keepSelectedIgnoreInActive,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [drawerTitle, setDrawerTitle] = useState<MessageDescriptor>(msg``);
  const [selectedFacet, setselectedFacet] = useState("");
  const { segmentProductListFilterOpened } = useSegment();

  const handleSelectFacet = (name: MessageDescriptor, facet: string) => {
    setDrawerTitle(name);
    setselectedFacet(facet);
    void segmentProductListFilterOpened(facet);
  };

  const handleCloseFilter = () => {
    setIsOpen(false);
  };

  // fix the 100vh fallback on mobile
  useEffect(() => {
    // First we get the viewport height and we multiple it by 1% to get a value for a vh unit
    const vh = window.innerHeight * 0.01;
    // Then we set the value in the --vh custom property to the root of the document
    document.documentElement.style.setProperty("--vh", `${vh}px`);
  }, []);

  return (
    <Wrapper>
      <LabelNavigation
        label={msg`Filters`}
        leftAdornment={<ArrowLeft strokeWidth="2" />}
        onClick={handleClose}
      />
      <FilterContent>
        <ContentWrapper>
          {availableFiltersList
            .filter(({ facet }) => {
              return !excludedFacetsList.includes(facet);
            })
            .map(({ name, icon, facet }: FiltersListItem) => (
              <MenuItem
                attribute={facet}
                key={facet}
                name={name}
                icon={icon}
                handleSelectFacet={handleSelectFacet}
                setIsOpen={setIsOpen}
                keepSelectedIgnoreInActive={keepSelectedIgnoreInActive}
                selectedCatSlug={selectedCatSlug}
              />
            ))}
        </ContentWrapper>

        <ClearAllRefinements
          onResetFilters={onResetFilters}
          handleClose={handleClose}
          excludedFiltersList={excludedFacetsList}
          keepSelectedIgnoreInActive={keepSelectedIgnoreInActive}
          setStatePayload={setStatePayload}
          selectedCatSlug={selectedCatSlug}
        />
      </FilterContent>
      <DrawerFilter
        isOpen={isOpen}
        onClose={handleCloseFilter}
        title={drawerTitle}
        selectedFacet={selectedFacet}
        genderId={capitalize(genderId)}
        setStatePayload={setStatePayload}
        keepSelectedIgnoreInActive={keepSelectedIgnoreInActive}
        selectedCatSlug={selectedCatSlug}
      >
        <Box
          overflowY="auto"
          display={selectedFacet === CATEGORIES_FACET ? "block" : "none"}
          padding={3}
        >
          <MultiselectCategoryWrapper
            setStatePayload={setStatePayload}
            selectedCatSlug={selectedCatSlug}
            attribute={CATEGORIES_FACET}
            genderId={genderId}
            limit={1000}
            isNewPlp={isNewPlp}
            departmentSlug={departmentSlug}
            categoriesTree={categoriesTree}
            isMultiselectCategories={isMultiselectCategories}
          />
        </Box>
        <Box
          overflowY="auto"
          display={selectedFacet === SIZE_FACET ? "block" : "none"}
        >
          <DefaultFilter
            attribute={SIZE_FACET}
            limit={100}
            transformItems={(
              items: Array<
                Hit<{
                  count: number;
                  isRefined: boolean;
                  label: string;
                  value: string[];
                }>
              >
            ) => orderBy(items, "label", "asc")}
          />
        </Box>

        {!excludedFacetsList.includes(BRAND_FACET) && (
          <Box
            overflowY="auto"
            display={selectedFacet === BRAND_FACET ? "block" : "none"}
          >
            <BrandFilter
              attribute={BRAND_FACET}
              limit={200}
              selectedFacet={selectedFacet}
            />
          </Box>
        )}

        <Box
          overflowY="auto"
          display={selectedFacet === CONSCIOUS_FACET ? "block" : "none"}
        >
          <GoodOnYouFilter attribute={CONSCIOUS_FACET} />
        </Box>

        <Box
          overflowY="auto"
          display={selectedFacet === PATTERN_FACET ? "block" : "none"}
        >
          <DefaultFilter
            attribute={PATTERN_FACET}
            transformItems={(
              items: Array<
                Hit<{
                  count: number;
                  isRefined: boolean;
                  label: string;
                  value: string[];
                }>
              >
            ) => orderBy(items, "label", "asc")}
          />
        </Box>

        <Box
          overflowY="auto"
          display={selectedFacet === STYLE_FACET ? "block" : "none"}
        >
          <DefaultFilter
            attribute={STYLE_FACET}
            transformItems={(
              items: Array<
                Hit<{
                  count: number;
                  isRefined: boolean;
                  label: string;
                  value: string[];
                }>
              >
            ) => orderBy(items, "label", "asc")}
          />
        </Box>

        <Box
          overflowY="auto"
          display={selectedFacet === PRICE_FACET ? "block" : "none"}
        >
          <Box px={4} py={3}>
            <PriceFilter attribute={PRICE_FACET} precision={2} />
          </Box>
        </Box>

        <Box
          overflowY="auto"
          display={selectedFacet === COLOUR_FACET ? "block" : "none"}
        >
          <ColorFilter
            attribute={COLOUR_FACET}
            limit={100}
            transformItems={(
              items: Array<
                Hit<{
                  count: number;
                  isRefined: boolean;
                  label: string;
                  value: string[];
                }>
              >
            ) => orderBy(items, "label", "asc")}
          />
        </Box>

        {!excludedFacetsList.includes(DISCOUNT_FACET) && (
          <Box
            overflowY="auto"
            display={selectedFacet === DISCOUNT_FACET ? "block" : "none"}
          >
            <DiscountFilter
              attribute={DISCOUNT_FACET}
              transformItems={(
                items: Array<
                  Hit<{
                    count: number;
                    isRefined: boolean;
                    label: string;
                    value: string[];
                  }>
                >
              ) => orderBy(items, "label", "asc")}
            />
          </Box>
        )}

        <ResultStats
          selectedFacet={selectedFacet}
          handleClose={handleCloseFilter}
          keepSelectedIgnoreInActive={keepSelectedIgnoreInActive}
          selectedCatSlug={selectedCatSlug}
        />
      </DrawerFilter>
    </Wrapper>
  );
};

export default MobileFilterView;
